import {
  FileFolderItem,
  isModelLoading,
  isModelVisible,
  useCapabilityRegistryContext,
  useDataContext,
  useFxWorkspaceContext,
} from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import { FitToViewModelCapabilityCoordinator } from '@trimble/web3d';
import { SelectionSyncCoordinator } from '@trimble/pane-sync';
import { ModelTree } from '@trimble/project-data-models-tree';
import { getTrbModelName } from '@trimble/web3d-providers';
import { JSX, Show, createMemo } from 'solid-js';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import { collectModelFolderDescendantRefTrns } from '../fx-workspace-draft-checkbox-sources';
import { fxWorkspaceDraftTrailingContent } from '../fx-workspace-draft-checkboxes';
import { handleNodeSelectionSync } from '../tree-helpers';
import { useLogger } from '@trimble/solidjs-utils';

export interface SharedModelsTreeContainerProps {
  onModelDetailsSelect?(model: FileFolderItem): void;
}

export const SharedModelsTreeContainer = (
  props: SharedModelsTreeContainerProps
): JSX.Element => {
  const { dataState, dataLoadingService, projectDataRepository } =
    useDataContext();
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const fxWorkspace = useFxWorkspaceContext();
  const logger = useLogger().getLogger('SharedModelsTreeContainer');

  const isModelsStorePending = createMemo(() => {
    if (projectDataRepository() !== undefined) {
      return false;
    }

    return dataState.tcFileFolder.state !== LoadState.LoadComplete;
  });

  const fitToViewCoordinator = capabilityRegistry.get(
    FitToViewModelCapabilityCoordinator
  );
  const selectionSyncCoordinator = capabilityRegistry.get(
    SelectionSyncCoordinator
  );

  const isNodeSelected = (nodeId: string): boolean =>
    selectionSyncCoordinator()
      ?.selectedModelIds()
      .has(getTrbModelName(nodeId)) ?? false;

  const handleNodeSelect = (
    nodeId: string,
    node: TreeNodeDefinition<FileFolderItem>,
    e: MouseEvent
  ): void => {
    void handleNodeSelectionSync(
      getTrbModelName(nodeId),
      node,
      e,
      (id) => isModelVisible(dataState, id),
      selectionSyncCoordinator()
    ).catch((error) => logger.error('Error selecting node', node, error));
  };

  return (
    <Show
      when={projectDataRepository()}
      keyed
      fallback={
        <Show when={isModelsStorePending()}>
          <div
            class="h-12 w-full"
            data-testid="models-tree-pending"
            aria-busy="true"
          />
        </Show>
      }
    >
      {(repository) => (
        <ModelTree
          repository={repository}
          isNodeLoading={(nodeId) => isModelLoading(dataState, nodeId)}
          isNodeVisible={(nodeId) => isModelVisible(dataState, nodeId)}
          onVisibilityToggle={(nodeId) => {
            if (
              isModelLoading(dataState, nodeId) ||
              isModelVisible(dataState, nodeId)
            ) {
              dataLoadingService.unload('model', nodeId);
            } else {
              dataLoadingService.load('model', nodeId);
            }
          }}
          onFitToView={(item) => {
            void fitToViewCoordinator()?.fitToView(getTrbModelName(item.id));
          }}
          onShowOnly={(item) => {
            dataLoadingService.loadExclusive('model', [item.id]);
          }}
          onSeeDetails={props.onModelDetailsSelect}
          isNodeSelected={isNodeSelected}
          onNodeSelect={handleNodeSelect}
          rowMode={fxWorkspace.isWorkspaceDataEditing() ? 'pick' : 'default'}
          visibleContent={(node) =>
            fxWorkspaceDraftTrailingContent(
              'models',
              node,
              fxWorkspace.isWorkspaceDataEditing(),
              (folderNodeId) =>
                collectModelFolderDescendantRefTrns(repository, folderNodeId)
            )
          }
        />
      )}
    </Show>
  );
};
